/**
 * A change of an environment's configuration planned over its current
 * values with a descriptor's fields, bounds and rules — platform-catalog
 * `contracts/README.md`, « Demande : deux notions de changement », « Phases »
 * and « Textes du moteur ».
 *
 * TWO NOTIONS OF CHANGE: a field is ASKED when the request gives it a value
 * other than the current one; it is MODIFIED when its final value differs
 * from the current one (asked, adjusted or fixed). A rule is evaluated only
 * when one of the fields it reads changes — asked for an adjustment,
 * modified for a constraint or a requirement — except a `require` with
 * `autofix`, applied at every request.
 *
 * THE PHASES, each in the descriptor's order, the refusals accumulated in
 * this order:
 *  1. adjustments (`set`) on the asked values, then the automatic fixes;
 *  2. the bounds of each modified field — an editor never touches a field
 *     whose CURRENT value is beyond the editor's bounds (an administrator's
 *     choice), else the final value must be within the caller's bounds;
 *  3. `growOnly`: a final value never below the current one;
 *  4. the constraints and requirements, their message a template over the
 *     final configuration.
 * The cluster's ceiling on the permanent quota is the caller's
 * (`environmentQuota.ts`, `quotaProblems`): it needs the quota's own inputs.
 *
 * A NEW environment (`planCreation`, the core's generic creation form,
 * 2026-10-25): the same phases, every field asked and modified, without the
 * administrator's locks nor `growOnly`.
 *
 * EVERY REFUSAL TWICE: its French text (`problems`, what the tasks and the
 * commits say, unchanged) and its key with its parameters
 * (`problemDetails`), so that a screen — the frontend runs this same plan —
 * says it in its own words and next to its field (contracts/README.md,
 * « Textes du moteur »).
 */

import {
  isDatabaseMode,
  withinBound,
  type AppTypeDescriptor,
  type ConfigurationGrant,
  type DatabaseMode,
  type DescriptorField,
  type FieldValue,
  type FieldValues,
  valueOf,
} from './descriptor';
import {
  evaluate,
  expressionFields,
  hasOwn,
  renderTemplate,
  truthy,
  type EvaluationContext,
  type ExpressionNode,
} from './expression';

/** The labels of the PostgreSQL modes: the template's `enumNames`. */
export const MODE_LABELS: Readonly<Record<DatabaseMode, string>> = {
  single: 'Instance unique',
  ha2: 'HA — 2 instances, deux zones (stricte)',
  ha3zones: 'HA — 3 instances, une par zone (stricte)',
};

/** How each unit is said in French (README, « Textes du moteur »). */
const UNIT_TEXT: Readonly<Record<string, string>> = {
  m: 'm',
  Mi: 'Mio',
  Gi: 'Gio',
  '%': '%',
  s: 's',
};

export function unitText(field: DescriptorField): string | undefined {
  return field.unit === undefined ? undefined : UNIT_TEXT[field.unit];
}

/** A value as the report and the refusals say it: « 768 Mio », « oui ». */
export function formatFieldValue(
  field: DescriptorField,
  value: FieldValue,
): string {
  if (typeof value === 'boolean') {
    return value ? 'oui' : 'non';
  }
  if (field.type === 'databaseMode' && isDatabaseMode(value)) {
    return MODE_LABELS[value];
  }
  const unit = unitText(field);
  return unit ? `${value} ${unit}` : String(value);
}

/** The bounds of a field, in words, for a refusal. */
function boundsText(field: DescriptorField, grant: ConfigurationGrant) {
  const bound = field.bounds[grant];
  if (!bound) {
    return '';
  }
  if (bound.kind === 'values') {
    return `valeurs permises : ${bound.values.join(', ')}`;
  }
  const unit = unitText(field);
  return `de ${bound.min} à ${bound.max}${unit ? ` ${unit}` : ''}${
    bound.step > 1 ? `, par pas de ${bound.step}` : ''
  }`;
}

/** One field that changes: asked by the caller, or derived by a rule. */
export interface ConfigurationChange {
  field: string;
  before: FieldValue;
  after: FieldValue;
  derived: boolean;
}

/**
 * One refusal, keyed (header). The keys of the engine: `locked` (`current`),
 * `bounds` (`min`, `max`, `step`), `values` (`allowed`, the values joined by
 * « , »), `growOnly` (`current`, `asked`) — each with its `field`; a rule's
 * is `rule:<id>`, with `left` and `right`, the two sides of its test when it
 * is a comparison, and `fields`, the fields its test reads. A rule without
 * `id` has no key: its text and its `fields` only.
 */
export interface PlanProblem {
  key?: string;
  field?: string;
  /**
   * A rule's: the configurable fields its test reads (not its `when`), in
   * the descriptor's order — where a screen without words for the rule
   * points it out.
   */
  fields?: string[];
  params: Record<string, FieldValue>;
  /** The text of `problems`, in French. */
  text: string;
}

export interface ConfigurationPlan {
  /** The configuration once written: the current one, the changes, the rules. */
  desired: FieldValues;
  /** In the order of the descriptor's fields; empty when nothing changes. */
  changes: ConfigurationChange[];
  /** Why the changes are refused, in French; empty when they are not. */
  problems: string[];
  /** The same refusals, in the same order, keyed (`PlanProblem`). */
  problemDetails: PlanProblem[];
}

const COMPARISONS: ReadonlySet<string> = new Set([
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>=',
]);

/** The two sides of a comparison, the parameters of its refusal. */
function comparisonSides(
  test: ExpressionNode,
  context: EvaluationContext,
): Record<string, FieldValue> {
  if (test.kind !== 'binary' || !COMPARISONS.has(test.op)) {
    return {};
  }
  return {
    left: evaluate(test.left, context),
    right: evaluate(test.right, context),
  };
}

/**
 * The changes asked over `current`, the rules applied, checked against the
 * caller's bounds (header). Only the configurable fields are read from
 * `changes` (`validateFieldValues` refuses any other first).
 */
export function planWithDescriptor(input: {
  descriptor: AppTypeDescriptor;
  current: FieldValues;
  changes: Readonly<Record<string, FieldValue | undefined>>;
  grant: ConfigurationGrant;
}): ConfigurationPlan {
  return planOver({ ...input, creation: false });
}

/**
 * A NEW environment's configuration — what a creation form chose —
 * checked as `planWithDescriptor` checks a change, with no current
 * configuration to compare it with: EVERY configurable field counts as
 * asked and as modified, so every adjustment whose `when` holds fires, the
 * automatic fixes apply, every field is checked against the caller's
 * bounds and every constraint and requirement is evaluated. Neither the
 * lock of an administrator's value (phase 2) nor `growOnly` (phase 3)
 * applies: nothing exists yet. `changes` lists every field, its `before`
 * the value given.
 *
 * `values` must hold every configurable field (`validateFieldValues` with
 * `requireAll` first): a missing one throws, as a missing current value
 * does in `planWithDescriptor`.
 */
export function planCreation(input: {
  descriptor: AppTypeDescriptor;
  values: FieldValues;
  grant: ConfigurationGrant;
}): ConfigurationPlan {
  return planOver({
    descriptor: input.descriptor,
    current: input.values,
    changes: input.values,
    grant: input.grant,
    creation: true,
  });
}

/** `planWithDescriptor`, or `planCreation` when `creation`. */
function planOver(input: {
  descriptor: AppTypeDescriptor;
  current: FieldValues;
  changes: Readonly<Record<string, FieldValue | undefined>>;
  grant: ConfigurationGrant;
  creation: boolean;
}): ConfigurationPlan {
  const { descriptor, changes, grant, creation } = input;
  const fields = descriptor.configurableFields;
  /** The current value of a configurable field, always read (`readConfiguration`). */
  const now = (id: string): FieldValue => {
    const value = valueOf(input.current, id);
    if (value === undefined) {
      throw new Error(`Valeur actuelle absente : ${id}`);
    }
    return value;
  };
  const current: FieldValues = {};
  for (const field of fields) {
    current[field.id] = now(field.id);
  }
  const desired: FieldValues = { ...current };
  for (const field of fields) {
    const value = valueOf(changes, field.id);
    if (value !== undefined) {
      desired[field.id] = value;
    }
  }
  const asked = (id: string) => {
    const value = valueOf(changes, id);
    return value !== undefined && (creation || value !== valueOf(current, id));
  };
  const testFields = (test: ExpressionNode) => {
    const read = expressionFields(test);
    return fields.filter(field => read.has(field.id)).map(field => field.id);
  };
  const rules = descriptor.rules.filter(
    rule => rule.grant === undefined || rule.grant === grant,
  );

  // 1. Adjustments on what is asked, then the automatic fixes.
  for (const rule of rules) {
    if (rule.kind !== 'adjust' || ![...rule.fields].some(asked)) {
      continue;
    }
    const context = { values: desired, changed: asked };
    if (rule.when && !truthy(evaluate(rule.when, context))) {
      continue;
    }
    desired[rule.set] = evaluate(rule.value, context);
  }
  const modified = (id: string) =>
    creation || valueOf(desired, id) !== valueOf(current, id);
  for (const rule of rules) {
    if (rule.kind !== 'autofix') {
      continue;
    }
    const context = { values: desired, changed: modified };
    if (
      truthy(evaluate(rule.when, context)) &&
      !truthy(evaluate(rule.require, context))
    ) {
      desired[rule.target] = rule.fixedValue;
    }
  }

  // 2. The bounds of each modified field.
  const details: PlanProblem[] = [];
  for (const field of fields) {
    if (!modified(field.id)) {
      continue;
    }
    if (
      !creation &&
      grant === 'editor' &&
      !withinBound(current[field.id], field.bounds.editor)
    ) {
      details.push({
        key: 'locked',
        field: field.id,
        params: { current: current[field.id] },
        text: `${field.label} : la valeur actuelle (${formatFieldValue(
          field,
          current[field.id],
        )}) est hors des bornes du formulaire de création, réservée aux administrateurs.`,
      });
      continue;
    }
    const bound = field.bounds[grant];
    if (bound && !withinBound(desired[field.id], bound)) {
      details.push({
        key: bound.kind === 'values' ? 'values' : 'bounds',
        field: field.id,
        params:
          bound.kind === 'values'
            ? { allowed: bound.values.join(', ') }
            : { min: bound.min, max: bound.max, step: bound.step },
        text: `${field.label} : ${formatFieldValue(
          field,
          desired[field.id],
        )} refusé (${boundsText(field, grant)})${
          grant === 'editor' ? ' ; au-delà, réservé aux administrateurs' : ''
        }.`,
      });
    }
  }

  // 3. The volumes that only grow.
  for (const field of fields) {
    const before = current[field.id];
    const after = desired[field.id];
    if (
      !creation &&
      field.growOnly &&
      typeof before === 'number' &&
      typeof after === 'number' &&
      after < before
    ) {
      const unit = unitText(field);
      const suffix = unit ? ` ${unit}` : '';
      details.push({
        key: 'growOnly',
        field: field.id,
        params: { current: before, asked: after },
        text: `${field.label} : un volume ne peut qu’être agrandi (${before}${suffix} actuellement, ${after}${suffix} demandé).`,
      });
    }
  }

  // 4. The constraints and requirements.
  for (const rule of rules) {
    if (rule.kind !== 'check' || ![...rule.fields].some(modified)) {
      continue;
    }
    const context = { values: desired, changed: modified };
    if (rule.when && !truthy(evaluate(rule.when, context))) {
      continue;
    }
    if (!truthy(evaluate(rule.test, context))) {
      details.push({
        ...(rule.id !== undefined ? { key: `rule:${rule.id}` } : {}),
        fields: testFields(rule.test),
        params: comparisonSides(rule.test, context),
        text: renderTemplate(rule.message, context),
      });
    }
  }

  const planned: ConfigurationChange[] = fields
    .filter(field => modified(field.id))
    .map(field => ({
      field: field.id,
      before: current[field.id],
      after: desired[field.id],
      derived: valueOf(changes, field.id) !== desired[field.id],
    }));
  return {
    desired,
    changes: planned,
    problems: details.map(detail => detail.text),
    problemDetails: details,
  };
}

/** One change as the report and the commit say it. */
export function changeLine(
  descriptor: AppTypeDescriptor,
  change: ConfigurationChange,
): string {
  const field = descriptor.fieldById.get(change.field);
  if (!field) {
    throw new Error(
      `Champ inconnu du type ${descriptor.name} : ${change.field}`,
    );
  }
  return `${field.label} : ${formatFieldValue(
    field,
    change.before,
  )} → ${formatFieldValue(field, change.after)}${
    change.derived ? ' (déduit)' : ''
  }`;
}

/** Whether `value` has the shape of `field`'s values (README, « Lecture »). */
export function valueFits(
  field: DescriptorField,
  value: unknown,
): value is FieldValue {
  switch (field.type) {
    case 'boolean':
    case 'databaseSync':
      return typeof value === 'boolean';
    case 'databaseMode':
      return isDatabaseMode(value);
    case 'enum':
      if (!field.numericEnum) {
        return typeof value === 'string';
      }
      break;
    default:
      break;
  }
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * TRANSITIONAL — field ids renamed by the Valkey renaming (plan of
 * 2026-10-25, decision 3), as `[before, after]`: during the alias period a
 * caller (a platform-catalog template, the Odoo module) and the descriptor
 * it is checked against may disagree on the name. TO REMOVE with the
 * `redis` alias of the contract (`kind: redis`, values block `redis:`).
 */
export const RENAMED_FIELD_IDS: ReadonlyArray<readonly [string, string]> = [
  ['haRedis', 'haCache'],
];

/**
 * `values` keyed as `descriptor` declares its fields: an id of
 * `RENAMED_FIELD_IDS` the descriptor does not declare, given while its
 * other name is declared and not given, takes that other name.
 */
export function withDeclaredFieldIds<V>(
  descriptor: AppTypeDescriptor,
  values: Readonly<Record<string, V>>,
): Record<string, V> {
  const out: Record<string, V> = { ...values };
  for (const [before, after] of RENAMED_FIELD_IDS) {
    for (const [given, declared] of [
      [before, after],
      [after, before],
    ]) {
      if (
        hasOwn(out, given) &&
        !hasOwn(out, declared) &&
        !descriptor.fieldById.has(given) &&
        descriptor.fieldById.has(declared)
      ) {
        out[declared] = out[given];
        delete out[given];
      }
    }
  }
  return out;
}

/**
 * `values` checked against the descriptor before anything is read — what the
 * strict zod schemas of the actions checked for Odoo's fields: every id a
 * field of the type (`configurable` ones only when `configurableOnly`), every
 * value of its shape (whole numbers ≥ 0, booleans, a PostgreSQL mode, an
 * enum's string). A renamed field id is read under the name the descriptor
 * declares (`withDeclaredFieldIds`). Throws, in French, with every refusal.
 */
export function validateFieldValues(
  descriptor: AppTypeDescriptor,
  values: Readonly<Record<string, unknown>>,
  options: { configurableOnly: boolean; requireAll: boolean },
): FieldValues {
  const problems: string[] = [];
  const out: FieldValues = {};
  const allowed = options.configurableOnly
    ? descriptor.configurableFields
    : descriptor.fields;
  const allowedIds = new Set(allowed.map(field => field.id));
  const given = withDeclaredFieldIds(descriptor, values);
  for (const [id, value] of Object.entries(given)) {
    const field = descriptor.fieldById.get(id);
    if (!field || !allowedIds.has(id)) {
      problems.push(
        `${id} : champ inconnu${field ? ' de la configuration' : ''} du type ${
          descriptor.name
        }`,
      );
      continue;
    }
    if (!valueFits(field, value)) {
      problems.push(`${id} : valeur refusée ${JSON.stringify(value)}`);
      continue;
    }
    out[id] = value;
  }
  if (options.requireAll) {
    for (const field of allowed) {
      if (!hasOwn(given, field.id)) {
        problems.push(`${field.id} : valeur manquante`);
      }
    }
  }
  if (problems.length > 0) {
    throw new Error(
      `Valeurs refusées pour le type ${descriptor.name} : ${problems.join(
        ' ; ',
      )}.`,
    );
  }
  return out;
}
